import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import { ArrowLeft, ArrowRight, Check, Building2, MapPin, Printer } from 'lucide-react';
import { parseApiError } from '../lib/parseApiError';
import WizardStepCliente from '../components/cliente/wizard/WizardStepCliente';
import WizardStepPuntiVendita from '../components/cliente/wizard/WizardStepPuntiVendita';
import WizardStepImpianti from '../components/cliente/wizard/WizardStepImpianti';
import WizardStepRiepilogo from '../components/cliente/wizard/WizardStepRiepilogo';
import BarcodeScannerModal from '../components/BarcodeScannerModal';
import { uid } from '../lib/uid';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const STEPS = [
  { id: 1, title: 'Dati Cliente', icon: Building2 },
  { id: 2, title: 'Punti Vendita', icon: MapPin },
  { id: 3, title: 'Impianti', icon: Printer },
  { id: 4, title: 'Riepilogo', icon: Check }
];

export default function ClienteWizardPage() {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [saving, setSaving] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [tecnici, setTecnici] = useState([]);
  const lastLookupRef = useRef('');

  // Step 1: Dati Cliente
  const [cliente, setCliente] = useState({
    ragione_sociale: '',
    partita_iva: '',
    codice_fiscale: '',
    sdi: '',
    indirizzo_legale: '',
    citta_legale: '',
    cap_legale: '',
    provincia_legale: '',
    telefono: '',
    email: '',
    pec: '',
    referente_nome: '',
    referente_email: '',
    referente_cellulare: '',
    tecnico_assegnato_id: '',
    tecnico_assegnato_nome: '',
    note: ''
  });

  // Step 2: Punti Vendita
  const [puntiVendita, setPuntiVendita] = useState([]);

  // Step 3: Impianti
  const [impianti, setImpianti] = useState([]);
  const [scannerOpen, setScannerOpen] = useState(false);
  const [scanIndex, setScanIndex] = useState(null);

  // Carica lista tecnici al mount
  useEffect(() => {
    axios.get(`${API}/tecnici`)
      .then(res => setTecnici(res.data || []))
      .catch(() => setTecnici([]));
  }, []);

  // Copia indirizzo legale del cliente nel punto vendita
  const copyAddressToPuntoVendita = (index) => {
    if (!cliente.indirizzo_legale && !cliente.citta_legale) {
      toast.error('Compila prima l\'indirizzo legale del cliente');
      return;
    }
    const updated = [...puntiVendita];
    updated[index] = {
      ...updated[index],
      nome: updated[index].nome || (cliente.ragione_sociale || 'Sede principale'),
      indirizzo: cliente.indirizzo_legale || '',
      citta: cliente.citta_legale || '',
      cap: cliente.cap_legale || '',
      provincia: cliente.provincia_legale || ''
    };
    setPuntiVendita(updated);
    toast.success('Indirizzo del cliente copiato');
  };

  // Validazione step
  const isStepValid = (step) => {
    switch (step) {
      case 1:
        return cliente.ragione_sociale.trim() !== '' && (cliente.tecnico_assegnato_nome || '').trim() !== '';
      case 2:
        return true; // Opzionale
      case 3:
        return true; // Opzionale
      case 4:
        return true;
      default:
        return false;
    }
  };

  const nextStep = () => {
    if (isStepValid(currentStep) && currentStep < 4) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Punto Vendita handlers
  const addPuntoVendita = () => {
    // Se non ci sono ancora punti vendita e l'indirizzo legale è compilato,
    // pre-popola il primo punto vendita con i dati del cliente
    const isFirst = puntiVendita.length === 0;
    const prefill = isFirst && (cliente.indirizzo_legale || cliente.citta_legale);
    setPuntiVendita([...puntiVendita, {
      _uid: uid(),
      nome: prefill ? (cliente.ragione_sociale || 'Sede principale') : '',
      indirizzo: prefill ? (cliente.indirizzo_legale || '') : '',
      citta: prefill ? (cliente.citta_legale || '') : '',
      cap: prefill ? (cliente.cap_legale || '') : '',
      provincia: prefill ? (cliente.provincia_legale || '') : '',
      referente_nome: prefill ? (cliente.referente_nome || '') : '',
      referente_telefono: prefill ? (cliente.referente_cellulare || cliente.telefono || '') : '',
      referente_email: prefill ? (cliente.referente_email || '') : '',
      note: ''
    }]);
    if (prefill) {
      toast.success('Punto vendita pre-compilato con i dati del cliente. Modificalo se necessario.');
    }
  };

  const updatePuntoVendita = (index, field, value) => {
    const updated = [...puntiVendita];
    updated[index][field] = value;
    setPuntiVendita(updated);
  };

  const removePuntoVendita = (index) => {
    // Rimuovi anche gli impianti associati
    const updatedImpianti = impianti.filter(imp => imp.punto_vendita_index !== index)
      .map(imp => ({
        ...imp,
        punto_vendita_index: imp.punto_vendita_index > index ? imp.punto_vendita_index - 1 : imp.punto_vendita_index
      }));
    setImpianti(updatedImpianti);
    setPuntiVendita(puntiVendita.filter((_, i) => i !== index));
  };

  // Impianto handlers
  const addImpianto = () => {
    setImpianti([...impianti, {
      _uid: uid(),
      punto_vendita_index: puntiVendita.length > 0 ? 0 : null,
      tipo: '',
      marca: '',
      modello: '',
      matricola: '',
      data_installazione: '',
      data_scadenza_verifica: '',
      note: '',
      abbonamento_attivo: false,
      abbonamento_descrizione: '',
      abbonamento_data_inizio: '',
      abbonamento_data_scadenza: '',
      abbonamento_importo: ''
    }]);
  };

  const updateImpianto = (index, field, value) => {
    const updated = [...impianti];
    updated[index][field] = value;
    setImpianti(updated);
  };

  const handleImpiantoScan = async (codice) => {
    if (scanIndex === null) return;
    try {
      const { data } = await axios.get(`${API}/magazzino/scan/${encodeURIComponent(codice)}`);
      if (data.stato === 'venduto') {
        toast.error(`⚠️ Cassa già assegnata a ${data.venduto_cliente_nome || 'altro cliente'}`);
        return;
      }
      setImpianti(prev => prev.map((imp, i) => i === scanIndex ? {
        ...imp,
        matricola: data.matricola || codice,
        marca: data.marca || imp.marca,
        modello: data.modello || imp.modello,
      } : imp));
      toast.success(`Trovato in magazzino: ${data.descrizione}`);
    } catch (error) {
      if (error?.response?.status === 404) {
        setImpianti(prev => prev.map((imp, i) => i === scanIndex ? { ...imp, matricola: codice } : imp));
        toast.info(`Matricola "${codice}" inserita (non in magazzino)`);
      } else {
        toast.error(parseApiError(error, 'Errore ricerca magazzino'));
      }
    }
  };

  const removeImpianto = (index) => {
    setImpianti(impianti.filter((_, i) => i !== index));
  };

  // Lookup automatico dati aziendali da Partita IVA (VIES)
  const lookupPartitaIva = async (pivaValue) => {
    const piva = (pivaValue || cliente.partita_iva).replace(/\D/g, '');
    if (piva.length !== 11) {
      toast.error('Inserisci una Partita IVA valida (11 cifre)');
      return;
    }
    lastLookupRef.current = piva;
    setLookupLoading(true);
    try {
      const res = await axios.get(`${API}/clienti/lookup-piva/${piva}`);
      const d = res.data;
      setCliente(prev => ({
        ...prev,
        partita_iva: d.partita_iva,
        ragione_sociale: d.ragione_sociale || prev.ragione_sociale,
        indirizzo_legale: d.indirizzo || prev.indirizzo_legale,
        cap_legale: d.cap || prev.cap_legale,
        citta_legale: d.citta || prev.citta_legale,
        provincia_legale: d.provincia || prev.provincia_legale
      }));
      toast.success('Dati aziendali compilati da VIES — verifica e modifica se necessario');
    } catch (error) {
      toast.error(parseApiError(error, 'Ricerca P.IVA non riuscita'));
    } finally {
      setLookupLoading(false);
    }
  };

  // Auto-lookup quando si raggiunge l'11ª cifra
  const handlePivaChange = (value) => {
    setCliente(prev => ({ ...prev, partita_iva: value }));
    const digits = value.replace(/\D/g, '');
    if (digits.length === 11 && lastLookupRef.current !== digits) {
      lookupPartitaIva(digits);
    }
  };

  // Salvataggio
  const handleSave = async () => {
    setSaving(true);
    try {
      // Prepara i dati
      const data = {
        ...cliente,
        email: cliente.email || null,
        pec: cliente.pec || null,
        referente_email: cliente.referente_email || null,
        punti_vendita: puntiVendita.filter(pv => pv.nome && pv.indirizzo && pv.citta).map(pv => ({
          ...pv,
          referente_email: pv.referente_email || null
        })),
        impianti: impianti.filter(imp => imp.tipo && imp.matricola).map(imp => ({
          ...imp,
          punto_vendita_index: imp.punto_vendita_index !== null ? parseInt(imp.punto_vendita_index) : null,
          abbonamento_importo: imp.abbonamento_importo ? parseFloat(imp.abbonamento_importo) : null
        }))
      };

      const response = await axios.post(`${API}/clienti/wizard`, data);
      
      toast.success(response.data.message);
      navigate(`/clienti/${response.data.data.cliente.id}`);
    } catch (error) {
      toast.error(parseApiError(error, 'Errore nel salvataggio'));
    } finally {
      setSaving(false);
    }
  };

  // Conta elementi validi per il riepilogo
  const validPuntiVendita = puntiVendita.filter(pv => pv.nome && pv.indirizzo && pv.citta);
  const validImpianti = impianti.filter(imp => imp.tipo && imp.matricola);
  const validAbbonamenti = impianti.filter(imp => imp.abbonamento_attivo && imp.abbonamento_data_inizio && imp.abbonamento_data_scadenza);

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="cliente-wizard">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Button variant="ghost" size="icon" className="rounded-none" onClick={() => navigate('/clienti')}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div>
          <h1 className="text-3xl font-black tracking-tight">Nuovo Cliente</h1>
          <p className="text-muted-foreground text-sm">Creazione guidata completa</p>
        </div>
      </div>

      {/* Progress Steps */}
      <div className="flex items-center justify-between border border-border p-4 bg-muted/30">
        {STEPS.map((step, index) => (
          <div key={step.id} className="flex items-center">
            <div 
              className={`flex items-center gap-2 cursor-pointer ${
                currentStep === step.id 
                  ? 'text-primary font-bold' 
                  : currentStep > step.id 
                    ? 'text-success' 
                    : 'text-muted-foreground'
              }`}
              onClick={() => step.id < currentStep && setCurrentStep(step.id)}
            >
              <div className={`w-8 h-8 rounded-full flex items-center justify-center border-2 ${
                currentStep === step.id 
                  ? 'border-primary bg-primary text-primary-foreground' 
                  : currentStep > step.id 
                    ? 'border-success bg-success text-white' 
                    : 'border-muted-foreground'
              }`}>
                {currentStep > step.id ? <Check className="h-4 w-4" /> : step.id}
              </div>
              <span className="hidden md:inline text-sm">{step.title}</span>
            </div>
            {index < STEPS.length - 1 && (
              <div className={`w-8 md:w-16 h-0.5 mx-2 ${
                currentStep > step.id ? 'bg-success' : 'bg-border'
              }`} />
            )}
          </div>
        ))}
      </div>

      {/* Step Content */}
      <Card className="rounded-none">
        <CardHeader>
          <CardTitle className="font-mono uppercase tracking-wider flex items-center gap-2">
            {(() => {
              const StepIcon = STEPS[currentStep - 1].icon;
              return <StepIcon className="h-5 w-5" />;
            })()}
            {STEPS[currentStep - 1].title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          {currentStep === 1 && (
            <WizardStepCliente
              cliente={cliente}
              setCliente={setCliente}
              tecnici={tecnici}
              lookupLoading={lookupLoading}
              onLookupPiva={lookupPartitaIva}
              onPivaChange={handlePivaChange}
            />
          )}

          {currentStep === 2 && (
            <WizardStepPuntiVendita
              puntiVendita={puntiVendita}
              onAdd={addPuntoVendita}
              onUpdate={updatePuntoVendita}
              onRemove={removePuntoVendita}
              onCopyAddress={copyAddressToPuntoVendita}
            />
          )}

          {currentStep === 3 && (
            <WizardStepImpianti
              impianti={impianti}
              validPuntiVendita={validPuntiVendita}
              onAdd={addImpianto}
              onUpdate={updateImpianto}
              onRemove={removeImpianto}
              onScan={(idx) => { setScanIndex(idx); setScannerOpen(true); }}
            />
          )}

          {currentStep === 4 && (
            <WizardStepRiepilogo
              cliente={cliente}
              validPuntiVendita={validPuntiVendita}
              validImpianti={validImpianti}
              validAbbonamenti={validAbbonamenti}
            />
          )}
        </CardContent>
      </Card>

      {/* Navigation Buttons */}
      <div className="flex justify-between">
        <Button 
          variant="outline" 
          className="rounded-none"
          onClick={prevStep}
          disabled={currentStep === 1}
        >
          <ArrowLeft className="h-4 w-4 mr-2" />
          Indietro
        </Button>
        
        {currentStep < 4 ? (
          <Button 
            className="rounded-none font-mono uppercase"
            onClick={nextStep}
            disabled={!isStepValid(currentStep)}
            data-testid="wizard-next-btn"
          >
            Avanti
            <ArrowRight className="h-4 w-4 ml-2" />
          </Button>
        ) : (
          <Button 
            className="rounded-none font-mono uppercase bg-success hover:bg-success/90"
            onClick={handleSave}
            disabled={saving || !cliente.ragione_sociale}
            data-testid="wizard-save-btn"
          >
            {saving ? 'Salvataggio...' : 'Salva Tutto'}
            <Check className="h-4 w-4 ml-2" />
          </Button>
        )}
      </div>

      <BarcodeScannerModal
        open={scannerOpen}
        onOpenChange={setScannerOpen}
        onScan={handleImpiantoScan}
        title="Inquadra il codice a barre della cassa"
      />
    </div>
  );
}
